import React from 'react'
import uuid from 'uuid/v4'

import { Translate } from 'react-redux-i18n'
import { TradingPaneValues, PositionInput } from '../types'
import { formatCurrencyDifference, formatPriceDifference, } from '../lib/currency-formatter'
import NumberInput from './NumberInput'
import {
  PriceExtended,
  Trade,
  Price,
  TradeCommandType,
  calculatePl,
  calcTpValue,
  calcSlValue,
  calcPendingPriceValue,
} from '@bdswiss/mt4-connector'
import HelpTooltip from './HelpTooltip'
import Spinner from './Spinner'

export interface Props {
  accountLoaded: boolean
  type: 'profit' | 'loss' | 'price'
  tradeType: TradeCommandType
  label: string
  valueInitial: number
  value: number
  currency: string
  locale: string
  asset: PriceExtended
  enabled?: boolean
  hideSwitch?: boolean
  position?: Trade
  isPending?: boolean
  positionInput?: PositionInput
  tradingValues?: TradingPaneValues
  profitLoss?: number
  profitLossPercent?: number
  setValue: (value: number) => void
  onInputValid: (inputValid: boolean) => void
}
export interface State {
  id: string
  enabled: boolean
  value: number
}

class PLInput extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.toggle = this.toggle.bind(this)
    this.newValue = this.newValue.bind(this)
    this.calcValue = this.calcValue.bind(this)
    this.state = {
      id: uuid(),
      enabled: this.props.enabled || false,
      value: this.props.valueInitial,
    }
  }

  public render() {
    if (!this.props.asset) {
      return <Spinner />
    }
    const {
      label,
      valueInitial,
      type,
      value,
      currency,
      locale,
      asset,
      position,
      profitLoss,
      profitLossPercent,
      tradeType,
      onInputValid,
    } = this.props
    let plNumbers: JSX.Element = <span />
    if (this.state.enabled) {
      if (position && !/limit/i.test(position.type)) {
        const pl =
          position.type === 'buy'
            ? calculatePl({ ...(asset as Price), mbid: this.state.value }, position, position.rate)
            : calculatePl({ ...(asset as Price), mask: this.state.value }, position, position.rate)
        if (position.investment && type !== 'price') {
          plNumbers = (
            <div className={'font-weight-medium ' + (pl < 0 ? 'negative' : 'positive')}>
              {formatCurrencyDifference(pl, currency, locale) +
                ' (' +
                (pl > 0 ? '+' : '') +
                ((+pl.toFixed(2) * 100) / position.investment!).toFixed(2) +
                '%)'}
            </div>
          )
        } else if (type !== 'price') {
          plNumbers = (
            <div className={'font-weight-medium ' + (pl < 0 ? 'negative' : 'positive')}>
              {formatCurrencyDifference(currency === 'CUD' ? pl * 100 : pl, currency, locale)}
            </div>
          )
        }
      } else if (type === 'price') {
        let priceDifference
        let differencePercent
        if (/buy/i.test(tradeType)) {
          priceDifference = this.state.value - asset.mask
          differencePercent = (priceDifference * 100) / asset.mask
        } else {
          priceDifference = this.state.value - asset.mbid
          differencePercent = (priceDifference * 100) / asset.mbid
        }
        plNumbers = (
          <div className={'font-weight-medium ' + (priceDifference! < 0 ? 'negative' : 'positive')}>
            <div style={{ marginTop: '1em' }}>
              <Translate value={'priceDistance'} />:
            </div>
            <span>
              {formatPriceDifference(
                priceDifference,
                asset.digits,
                asset.currency,
                priceDifference! >= 0
              ) +
                ' (' +
                (priceDifference! >= 0 ? '+' : '') +
                differencePercent.toFixed(2) +
                '%)'}
            </span>
          </div>
        )
      } else if (profitLoss !== undefined) {
        plNumbers = (
          <div className={'font-weight-medium ' + (profitLoss! < 0 ? 'negative' : 'positive')}>
            {formatCurrencyDifference(profitLoss, currency, locale) +
              ' (' +
              (profitLoss! > 0 ? '+' : '') +
              profitLossPercent!.toFixed(2) +
              '%)'}
          </div>
        )
      }
    }
    return (
      <div className="counter-group">
        <div className="d-flex flex-row justify-content-between pt-3">
          <div className="counter-group__label d-flex flex-row align-items-center">
            {this.props.hideSwitch ? (
              undefined
            ) : (
              <>
                <input
                  id={this.state.id}
                  type="checkbox"
                  className="counter-group__checkbox"
                  defaultChecked={this.state.enabled}
                />
                <label
                  htmlFor={this.state.id}
                  onClick={this.toggle}
                  data-cy={'checkbox' + label.replace(/ /g, '')}
                />
              </>
            )}
            {label}{' '}
            <HelpTooltip
              content={
                { profit: 'helpTakeProfit', loss: 'helpStopLoss', price: 'helpOpenPrice' }[
                  this.props.type
                ] || ''
              }
            />
          </div>
          {type !== 'price' && plNumbers}
        </div>
        <NumberInput
          placeholder={label}
          valueInitial={valueInitial}
          value={value}
          step={Math.pow(10, -asset.digits)}
          precision={asset.digits}
          setValue={this.newValue}
          calcValue={this.calcValue}
          onInputValid={onInputValid}
          min={type === 'profit' ? 0.0001 : this.calcValue(Number.MIN_SAFE_INTEGER)}
          max={this.calcValue(Number.MAX_SAFE_INTEGER)}
        />
        {type === 'price' && plNumbers}
      </div>
    )
  }

  public componentDidMount() {
    const { type, position, enabled } = this.props
    this.setState(prevState => ({
      enabled: position ? (type === 'loss' ? position.sl !== 0 : position.tp !== 0) : !!enabled,
    }))
  }

  public componentDidUpdate(prevProps: Props, prevState: State) {
    const { enabled, position, value } = this.props
    if (prevProps.value !== value) {
      this.setState(prev => ({
        value,
        enabled: position ? value !== 0 : !!enabled,
      }))
    }
    if (prevProps.enabled !== enabled) {
      this.setState(prev => ({
        enabled: !!enabled,
      }))
    }
  }

  private toggle() {
    if (this.state.enabled) {
      this.props.setValue(0)
    } else {
      if (this.state.value) {
        this.props.setValue(this.state.value)
      } else {
        this.props.setValue(this.props.valueInitial)
        this.setState(prevState => ({
          value: this.props.valueInitial,
        }))
      }
    }
    this.setState(prevState => ({
      enabled: !prevState.enabled,
    }))
  }

  private calcValue(value: number): number {
    const { type, tradeType, asset, position, tradingValues, positionInput, isPending } = this.props
    const trade = position ? (position.type.includes('buy') ? 'buy' : 'sell') : tradeType
    // prettier-ignore
    const price = position
      ? isPending
        ? (positionInput && positionInput.priceInput) || position.openPrice
        : /buy/i.test(trade)
          ? type === 'profit' ? Math.max(position.openPrice, asset.mbid) : undefined
          : type === 'profit' ? Math.min(position.openPrice, asset.mask) : undefined
      : tradingValues
        ? tradingValues.pendingOrderEnabled
          ? tradingValues.price
          : undefined
        : undefined
    // prettier-ignore
    return type === 'profit'
      ? calcTpValue(trade, asset, value, price, isPending)
      : type === 'loss'
        ? calcSlValue(trade, asset, value, price, isPending)
        : calcPendingPriceValue(trade, asset, value)
  }

  private newValue(value: number) {
    if (this.state.enabled) {
      this.props.setValue(value)
    }
    this.setState(prevState => ({
      // enabled: true,
      value,
    }))
  }
}

export default PLInput
